import React from 'react'
import styled, { css } from 'styled-components'
import { ButtonStyleProps } from 'styled-system'

import BaseButton from 'components/Buttons/Button'
import { base } from '../Buttons/Button/variants'
import Icon from 'components/Icon'

import { WidthProps } from 'types/styled'
import { ThemeColor, colors } from 'utils/colors'
import { Z_INDEX } from 'utils/constants'

import { MenuPositionProps, OptionContainerProps } from './types'
import Box from 'components/Box'

export const Button = styled(BaseButton)<
  ButtonStyleProps & { isOpen: boolean; isHighlighted: boolean; bgColor: ThemeColor | undefined }
>((p) =>
  css({
    ...base,
    background: p.isHighlighted
      ? p.theme.colors.blue5
      : p.isOpen
      ? p.theme.colors.gray5
      : p.bgColor
      ? p.theme.colors[p.bgColor]
      : p.theme.colors.white,
    borderColor: p.isHighlighted ? p.theme.colors.blue1 : p.isOpen ? p.theme.colors.gray5 : p.theme.colors.white,
    color: p.isHighlighted ? p.theme.colors.blue1 : p.isOpen ? p.theme.colors.white : p.theme.colors.gray5,
    padding: '10px 10px 9px',
    maxHeight: '40px',
    display: 'flex',
    whiteSpace: 'nowrap',
    alignItems: 'center',

    ...(!p.isHighlighted && {
      // Hover style
      '&:hover': {
        background: p.isOpen ? undefined : p.theme.colors.white,
        borderColor: p.isOpen ? undefined : p.theme.colors.gray5,
      },

      // Active Focus style
      '&:active': {
        background: p.theme.colors.gray5,
        borderColor: p.theme.colors.gray5,
      },
    }),

    // Disabled style
    '&:disabled': {
      background: p.theme.colors.white,
      borderColor: p.theme.colors.white,
      color: p.theme.colors.gray2,

      '&:hover': {
        borderColor: p.theme.colors.gray2,
      },
    },
  }),
)

export const Container = styled.div<{ isDisabled?: boolean; inline?: boolean; width?: WidthProps }>`
  cursor: ${(p) => (p.isDisabled ? 'default' : 'pointer')};
  display: ${(p) => (p.inline ? 'inline-block' : 'block')};
  position: relative;
  user-select: none;
  ${(p) => p.width && `width: ${p.width}px;`}
`

export const LabelContainer = styled.div<{ labelColor?: ThemeColor; showAvatar?: boolean }>`
  ${(p) => p.theme.font({ size: 14, weight: 'normal' })}
  ${(p) => (p.labelColor ? `color: ${p.theme.colors[p.labelColor]};` : '')}
  align-items: center;
  display: flex;
  flex-grow: 1;
  min-height: 24px;
  max-width: 100%;
  gap: ${({ showAvatar }) => (showAvatar ? '10px' : '0')};
  width: 100%;
`

export const MenuListContainer = styled.ul<{ isSearchable?: boolean }>`
  border-radius: 4px;
  height: ${(p) => (p.isSearchable ? 'calc(100% - 66px)' : '100%')};
  max-height: 30vh;
  min-width: 8.5vw;
  overflow-y: auto;
  scroll-behavior: smooth;
`

const getOptionBg = ({
  isDisabled,
  isFocused,
  isMultiple,
  isSelected,
  isGroupLabel,
}: OptionContainerProps): ThemeColor => {
  if (isGroupLabel) {
    return 'gray1'
  }

  if (isDisabled) {
    return 'gray1'
  }

  if (isMultiple) {
    if (isFocused) {
      return 'gray2'
    }

    return 'white'
  }

  if (isSelected) {
    return 'blue5'
  }

  if (isFocused) {
    return 'gray2'
  }

  return 'white'
}

const getColor = ({ isDisabled, isSelected, isMultiple }: OptionContainerProps): string => {
  if (isMultiple) {
    return colors['defaultText']
  }

  if (isDisabled) {
    return isSelected ? colors['blue6'] : colors['gray4']
  }

  return isSelected ? colors['blue1'] : colors['defaultText']
}

export const Option = styled.li<OptionContainerProps>`
  ${(p) => p.theme.font({ size: 14 })}
  align-items: center;
  background: ${(p) => p.theme.colors[getOptionBg(p)]};
  box-sizing: border-box;
  color: ${getColor};
  cursor: ${(p) => (p.isGroupLabel ? 'default' : p.isDisabled ? 'not-allowed' : 'pointer')};
  display: flex;
  max-width: 100%;
  padding: ${(p) => (p.isDense ? `5px 35px` : p.isSubmenu ? '10px' : `10px 35px`)};
  padding-left: ${(p) => (p.isDense ? 5 : 12) + (p.isIndented ? 20 : 0)}px;
  position: relative;
  text-align: left;
  user-select: none;
  word-break: break-word;
  ${(p) =>
    !p.isSelected &&
    css`
      &:hover {
        background: ${(p) => p.theme.colors.gray1};
      }
    `}
`

export const PopupContainer = React.forwardRef<HTMLDivElement, MenuPositionProps>((props, ref) => (
  <StyledPopupContainer ref={ref} {...props} />
))

const StyledPopupContainer = styled.div<MenuPositionProps>`
  background-color: ${(p) => p.theme.colors.white};
  border-radius: 4px;
  box-shadow: 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05);
  max-width: ${(p) => (p.maxWidth ? p.maxWidth : '40vw')};
  width: ${(p) => (typeof p.width === 'number' ? `${p.width}px` : 'max-content')};
  z-index: ${(p) => (p.isVisible ? Z_INDEX.dropdown : -1)};
  visibility: ${(p) => (p.isVisible ? 'visible' : 'hidden')};
  opacity: ${(p) => (p.isVisible ? 1 : 0)};
  pointer-events: ${(p) => (p.isVisible ? 'auto' : 'none')};
`

export const CaretIcon = styled(Icon)`
  margin-left: 10px;
`

export const Marker = styled(Icon)`
  position: absolute;
`

export const SelectedMarker = styled(Marker)`
  right: 12px;
`

export const NodeSelectedMarker = styled(SelectedMarker)`
  margin-top: 10px;
`

export const ButtonTextWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 0.2rem;
`

export const ChildWrapper = styled.div`
  max-width: fit-content;
`

export const StyledItem = styled(Box)<{
  isSubmenu?: boolean
  isDense?: boolean
  top?: string | number
}>`
  border-bottom: ${({ isDense }) => (isDense ? 'none' : `1px solid ${colors.gray3}`)};

  ${({ isSubmenu, top }) =>
    isSubmenu &&
    css`
      position: absolute;
      left: 95%;
      top: ${typeof top === 'number' ? `${top}px` : top ?? '0'};
      width: 100%;
      box-shadow: 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08),
        0 9px 28px 8px rgba(0, 0, 0, 0.05);
    `}
`
